<script setup>
import { get_trait, get_package } from '@/services/option';
import { create_intervention } from '@/services/interventions';
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { get_terms } from '@/services/terms';
import { sortAgeGroup, swalError } from '@/helper';

const route = useRoute()
const router = useRouter()
const isLoading = ref(true)
const package_config = ref({})
const trait = ref({})
const intervention = ref({
  name: '',
  code: '',
  trait_id: '',
  document: ''
})
const intervention_term = ref()
const ageGroupOption = ref([])

onMounted(async ()=>{
  intervention.value.trait_id = route.params.trait_id
  let response = await get_package(route.params.package_id)
  if (response.error) {
    if (!response.isRedirecting) swalError(response.message || 'Failed to load package')
    return
  }
  package_config.value = response.result
  
  let trait_response = await get_trait(route.params.trait_id)
  if (trait_response.error) {
    if (!trait_response.isRedirecting) swalError(trait_response.message || 'Failed to load trait')
    return
  }
  trait.value = trait_response.result
  
  let age_group_response = await get_terms('age_group_int')
  if (age_group_response.error) {
    if (!age_group_response.isRedirecting) swalError(age_group_response.message || 'Failed to load age groups')
    return
  }
  ageGroupOption.value = generateOption(age_group_response.result.data.data)
  setAgeGroupValue()
  isLoading.value = false
})

function setAgeGroupValue(){
  let temp_age_group = route.query.age_group
  if (temp_age_group){
    let isFound = ageGroupOption.value.find(item => item.label_id == temp_age_group)
    if (isFound){
      intervention_term.value = isFound.valueProp
    }
  }
}

function generateOption(reponse){
  reponse = reponse.map(item => {
    return {
      valueProp: item.relationships.term_taxonomy.data.id,
      label: item.attributes.name,
      label_id: item.id,
      disabled: false
    }
  })
  return sortAgeGroup(reponse)
}

// const uploadedFile = ref()
function validateReport(event) {
  const selectedFile = event.target.files[0]; // Get the first selected file
  intervention.value.document = selectedFile
  if (intervention.value.document ) {
    // console.log("🚀 ~ file: download_report.vue:20 ~ validateReport ~ uploadedFile.value:", uploadedFile.value)
  }
}

const isSubmitting = ref(false)
async function createIntervention(){
  let isPass = checkForm()
  if (isPass.error){
    swalError(isPass.msg)
    return
  }
  isSubmitting.value = true
  const interventionParams = {
    intervention_obj:{
      ...intervention.value,
      intervention_activities_attributes: activities.value
    },
    term_relationship:{
      term_taxonomy_id: intervention_term.value
    }
  }
  let response = await create_intervention(interventionParams)
  isSubmitting.value = false
  if (response.error){
    if (!response.isRedirecting) {
      // Provide a descriptive error message depending on the failure type.
      let errorMsg = 'Failed to create intervention'
      if (response.message && response.message.includes('timeout')) {
        errorMsg = 'Request timed out. The file may be too large or the server is slow. Please try again.'
      } else if (response.message && response.message.includes('Network Error')) {
        errorMsg = 'Network error. Please check your internet connection and try again.'
      } else if (response.result?.errors) {
        errorMsg = Object.values(response.result.errors).flat().join(', ')
      } else if (response.result?.error) {
        errorMsg = response.result.error
      } else if (response.message) {
        errorMsg = response.message
      }
      swalError(errorMsg, true)
    }
    return
  }
  router.push({
    name: 'edit-intervention',
    params: {id: response.result.data.data.id}
  })
}

function checkForm(){
  let isPass = {error: false, msg: ''}
  if (intervention.value.name == ''){
    isPass.msg = 'Form is not completed'
    isPass.error = true
  }else if(intervention.value.document == ""){
    isPass.msg = 'File is not uploaded'
    isPass.error = true
  }else if(temp_activity.value != ''){
    isPass.msg = 'Check your activity description'
    isPass.error = true
  }else if(intervention_term.value == null){
    isPass.msg = 'Check your age group option'
    isPass.error = true
  }
  return isPass
}

function clearFileInput(){
  let fileInput = document.getElementById('file_input')
  fileInput.value = null
  intervention.value.document = ""
}

const temp_activity = ref('')
const activities = ref([])
function addActivity(){
  activities.value.push({title: temp_activity.value, id: '', _destroy: false})
  temp_activity.value = ''
}

function removeActivity(index, indicatorObj, indicators){
  if (indicatorObj.id == ''){
    activities.value.splice(index, 1)
  }else{
    activities.value[index]._destroy = true
    indicators = indicators.map(item =>{
      if (item === indicatorObj){
        item._destroy = 'true'
      }
      return item
    })
  }
}
</script>

<template>
  <div class="container-fluid">
    <Breadcrumbs 
      main="Interventions" 
      :title="trait.data?.attributes.name + ' - New Intervention'"
      :mainRoute="{name: 'package-intervention-list'}"
      :titleRoute="{name: 'intervention-list', params: {package_id: route.params.package_id, trait_id: route.params.trait_id}}"
    />

    <LoadingCard v-if="isLoading" />
    <template v-else>
      <!-- Page title -->
      <div class="d-flex align-items-center">
        <div>
          <h4 class="mb-0">Add intervention for {{ trait.data.attributes.name }}</h4>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-9">
          <!-- General Form -->
          <div class="card">
            <div class="card-body">
              <div class="form-items mt-3">
                <div class="row">
                  <div class="col-3 d-none">
                    <label class="form-label" for="code">Code</label>
                    <input v-model="intervention.code" class="form-control" id="code" type="text" placeholder="Code" required>
                  </div>
                  <div class="col-12">
                    <label class="form-label" for="intervention_description">Intervention Description</label>
                    <textarea rows="4" v-model="intervention.name"
                      id="intervention_description" placeholder="Intervention Description" 
                      class="form-control"
                      required
                    ></textarea>
                  </div>
                </div>
    
                <div v-auto-animate class="mt-4 row">
                  <!-- <div class="col-md-12"> -->
                  <label class="form-label" for="age">Intervention document</label>
                  <div class="row align-items-center">
                    <div class="col-md-9">
                      <input 
                        class="form-control" 
                        type="file" 
                        id="file_input"
                        aria-label="dna report" 
                        required=""
                        @change="validateReport"
                        accept="application/pdf"
                      >
                    </div>
                    <button class="btn btn-icon btn-light-danger" @click="clearFileInput()">
                      <vue-feather type="trash" size="1rem"/>
                    </button>
                  </div>
                  <!-- </div> -->
                </div>
              </div>
            </div>
          </div>

          <!-- Activity -->
          <div class="col-12 card">
            <div class="card-header pb-2 pt-3">
              <h5 class="mb-0">Activity</h5>
              <p class="mb-0 text-secondary">Add activity list for intervention</p>
            </div>
            <div class="card-body">
              <form @submit.prevent="addActivity()" class="row mt-3">
                <div class="col-8">
                  <input class="form-control" 
                    type="text" 
                    placeholder="Activity Description" required
                    v-model="temp_activity"
                  >
                </div>
                <div class="col-2">
                  <button type="submit" class="btn-rounded-icon p-0 btn btn-success"
                  >
                    <vue-feather type="plus"></vue-feather>
                  </button>
                </div>
              </form>
              <!-- items -->
              <div v-for="(activity, index) in activities" class="row mt-2">
                <template v-if="!activity._destroy">
                  <div class="col-8">
                    <input class="form-control" 
                      type="text" 
                      placeholder="Activity Description" required
                      v-model="activity.title"
                    >
                  </div>
                  <div class="col-2">
                    <button class="btn-rounded-icon p-0 btn btn-danger"
                      @click="removeActivity(index, activity, activities)"
                    >
                      <vue-feather type="x"></vue-feather>
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Submit & Age Group -->
        <div class="col-3">
          <div class="card">
            <div class="card-body">
              <div class="row mb-3">
                <label class="form-label" for="age_group_from">Age Group</label>
                <multiselect 
                  v-model="intervention_term" 
                  placeholder="Select age group"
                  :options="ageGroupOption"
                  valueProp="valueProp"
                  searchable="true"
                ></multiselect>
              </div>
              <button class="btn btn-light-success"
                @click="createIntervention()"
                :disabled="isSubmitting"
              >
                <div v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <template v-else>
                  Create Intervention
                </template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>